import ReactDOMServer from "react-dom/server";
import { ReportFieldDataTypes } from "../models/Reporting/ReportDataField";
import {
    ReportDatum,
    SignalReportDatum,
} from "../models/Reporting/ReportDatum";
import { FieldUse } from "../models/Reporting/ReportField";
import { ReportModel, VizType } from "../models/Reporting/ReportModel";
import theme from "../Theme/AppTheme";
import { getCurrencyCharacter, getDelimiterCharacter } from "./helpers";
import { isNumeric } from "./StringUtils";
import moment from "moment";
import { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import { ReportFormatOptions } from "components/Signals/Reports/ReportFormatOptions";

const defaultColorScheme = [
    theme.palette.green.main,
    theme.palette.blue.main,
    theme.palette.orange.main,
    theme.palette.red.main,
];
const singleSeriesMainBarColors = [
    theme.palette.secondary.main,
    theme.palette.primary.main,
];

const AnnotationRoleColumn = {
    type: "string",
    role: "annotation",
};

const StyleRoleAnnotation = { role: "style" };

// maps series name to y value
type DataPoint = Map<string, number | null>;

// Used for reports V2
export function reportDataToGoogleFormatV2(
    data: ReportDatum[],
    report: ReportModel | undefined,
    isWidget?: boolean,
    singleColor?: boolean,
    swapColors?: boolean,
    notTransparent?: boolean,
    isClassifier?: boolean,
    showPercent?: boolean,
) {
    const seriesNamesSet = new Set<string>();
    const xValuesSet = new Set<string>();
    const dataPoints = new Map<string, DataPoint>();

    const formattingOptions = ReportFormatOptions.fromJson(report?.vizOptions);

    const xField = report?.reportFields.find(
        (value) => value.fieldUse === FieldUse.X,
    );
    // const yField = report?.reportFields.find(value => value.fieldUse === FieldUse.Y);
    const xFieldType = xField?.reportDataField?.datatype;

    for (const datum of data) {
        const thisSeriesName = datum.grouping || "";
        const thisXValue = datum.x;
        seriesNamesSet.add(thisSeriesName);
        xValuesSet.add(thisXValue);

        const thisDataPoint =
            dataPoints.get(datum.x) ?? new Map<string, number>();
        thisDataPoint.set(thisSeriesName, datum.y);
        dataPoints.set(datum.x, thisDataPoint);
    }

    // We need to add null values to the result array so that they can be interpolated by the chart
    const seriesNames = Array.from(seriesNamesSet);
    const xValues = Array.from(xValuesSet);

    // header row looks like [Xlabel, S1Name, style, annotation, tooltip, S2Name, style, annotation, tooltip, S3Name, style, annotation, tooltip]

    const columnHeaders: any[] = [];
    const showDataLabels: boolean =
        !isWidget && !report?.formattingValues?.hideDataLabels;

    for (const seriesName of seriesNames) {
        columnHeaders.push(seriesName);
        if (
            report?.vizType !== VizType.Histogram &&
            (seriesNames.length > 1 || report?.vizType !== VizType.Line)
        ) {
            columnHeaders.push(StyleRoleAnnotation);
            if (showDataLabels) {
                columnHeaders.push(AnnotationRoleColumn);
            }
        }
    }

    const headerRow = ["x"].concat(columnHeaders) as any[];
    const result: (string | number | null | any)[][] = [];

    let colorScale: string[] =
        seriesNames.length > 1 ? defaultColorScheme : singleSeriesMainBarColors;

    if (report?.vizOptions?.colorScheme?.length) {
        colorScale = report?.vizOptions?.colorScheme;
    }

    if (swapColors) {
        colorScale = [theme.palette.blue.main, theme.palette.green.main];
    }
    if (isClassifier) {
        colorScale = [theme.palette.blue.main];
    }
    let colorIndex = 0;

    // const horizontalTicks:Array<{v:number|string,f:string}> = [];

    for (const xValue of xValues) {
        if (seriesNames.length > 1) {
            colorIndex = 0;
        }

        // result rows look like [XValue, S1Value, S2Value, S3Value]
        // const xVar = report?.vizType === VizType.Pie ? ({v: xValue?.toString(),
        //     f: xValue?.toString()}) : ({v:0, f: xValue});

        const xString = function (xValue: any) {
            let returnValue = xValue;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            xValue !== null ? (returnValue = xValue.toString()) : "";
            return returnValue;
        };
        const resultRow: (string | number | null | any)[] = [
            report?.vizType === VizType.Pie
                ? xString(xValue)
                : isNumeric(xString(xValue))
                ? formatChartValue(Number(xValue), report)
                : xValue,
        ];

        for (const seriesName of seriesNames) {
            const yValue = dataPoints.get(xValue)?.get(seriesName) ?? null;
            resultRow.push(yValue);
            if (
                report?.vizType !== VizType.Histogram &&
                (seriesNames.length > 1 || report?.vizType !== VizType.Line)
            ) {
                resultRow.push("__STYLE__");
                if (showDataLabels) {
                    if (yValue && Math.abs(yValue) > 0.025) {
                        let formattedValue = formatChartValue(yValue, report);
                        // Handle percent
                        if (
                            showPercent ||
                            formattingOptions.enablePercentFormat
                        ) {
                            formattedValue = `${formattedValue}%`;
                        }
                        resultRow.push(formattedValue);
                    } else {
                        resultRow.push("");
                    }
                }
            }
        }
        result.push(resultRow);
    }

    if (xFieldType !== ReportFieldDataTypes.Date) {
        result.sort((a, b) => {
            let x = a[1],
                y = b[1];
            if (a[1]?.f !== undefined) {
                x = a[1].f;
                y = b[1].f;
            }
            if (typeof x === "number" && typeof y === "number") {
                return -1 * (x - y);
            } else {
                return (x?.toString()?.toLowerCase() ?? "") >
                    (y?.toString()?.toLowerCase() ?? "")
                    ? 1
                    : -1;
            }
        });
    }

    const styleColumns = headerRow.findAllIndexes(
        (value, key) => value.role === "style",
    );

    for (const resultElement of result) {
        for (const styleColumn of styleColumns) {
            (resultElement as any[])[styleColumn] = `stroke-color: ${
                singleColor
                    ? theme.palette.secondary.main
                    : colorScale[colorIndex] ??
                      colorScale[colorIndex % colorScale.length]
            }; stroke-opacity: 1.0; stroke-width: 2; fill-color: ${
                singleColor
                    ? theme.palette.secondary.main
                    : colorScale[colorIndex] ??
                      colorScale[colorIndex % colorScale.length]
            }; fill-opacity: ${notTransparent ? "1" : "0.22"}`;
            if (seriesNames.length > 1) {
                colorIndex = (colorIndex + 1) % seriesNames.length;
            } else {
                colorIndex++;
            }
        }
    }

    result.insertAt(0, headerRow);
    // console.log(result);
    return result;
}

function adjustDecimal(unformatedNumber, report?: ReportModel): string {
    let returnValue = unformatedNumber.toString();
    if (Math.floor(unformatedNumber) !== unformatedNumber) {
        if (report?.formattingValues?.decimal) {
            returnValue = unformatedNumber.toFixed(
                report.formattingValues.decimal,
            );
        } else {
            returnValue = unformatedNumber.toFixed(2);
        }
    }
    return returnValue;
}

export function formatChartValue(
    inputValue: number,
    report: ReportModel | undefined,
) {
    let formattedValue = `${inputValue}`;
    if (report && !report?.formattingValues) {
        if (!Number.isNaN(inputValue)) {
            // Handle decimals
            formattedValue = adjustDecimal(inputValue, report);
            return formattedValue;
        } else {
            return formattedValue;
        }
    }
    if (!Number.isNaN(inputValue)) {
        // Handle decimals
        formattedValue = adjustDecimal(inputValue, report);
        // Handle delimiter
        if (report && report.formattingValues?.delimiter !== null) {
            const [valueWithoutDecimals, decimalValues] =
                formattedValue.split(".");

            const tempArray = valueWithoutDecimals.split("");
            for (let i = tempArray.length - 4; i >= 0; i -= 3) {
                tempArray[i] =
                    tempArray[i] +
                    getDelimiterCharacter(report.formattingValues?.delimiter);
            }
            formattedValue =
                tempArray.join("") + (decimalValues ? "." + decimalValues : "");
        }
        // Handle currency
        if (report && report?.formattingValues?.currency !== null) {
            formattedValue =
                getCurrencyCharacter(report?.formattingValues?.currency) +
                formattedValue;
        }
    }
    return formattedValue;
}

interface IGoogleFormatOptions {
    data: SignalChartDatum[];
    showPercent?: boolean;
    groupingIdentifierToLegendName?: Map<string, string>;
    isComparePrev?: boolean;
    tooltipBuilder?: (
        x: any,
        y: any,
        seriesName: any,
        datum: SignalChartDatum | undefined,
        point: SignalChartPointIdentity,
    ) =>
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | string;
    isTemporal?: boolean;
    temporalGrain?: SignalsTemporalLabels;
    useLabelsAsGroupingIds?: boolean;
    swapGroupingAndX?: boolean;
    filterMissingGroupedSeries?: boolean;
    /**
     * Leaves x values as the strings they arrived as, instead of reading date-shaped ones into
     * `Date` objects.
     *
     * Dated x values are converted because a chart with a date axis needs real dates to space and
     * tick from. A chart grouped by date already carries the labels it should show, the spans an
     * over-time chart would name, and only some spans look like a date at all. Converting them would
     * leave the axis mixing converted and unconverted values.
     */
    preformattedXValues?: boolean;
}

export type SignalChartDatum = Omit<SignalReportDatum, "y"> & {
    y: number | null;
};

interface GoogleFormat {
    data: any[][];
    seriesNames: Set<string>;
}

// Parses the x value to a Date object if it is a valid date string
// or returns the string value as is if it is not a date
const parseXValue = (value: string | number | null | undefined) => {
    const stringValue =
        value !== null && value !== undefined ? value.toString() : "";

    // Handle edge cases for Dates
    // Identify if the string is a date in the format of "MMM DD, YYYY" or "MMM YYYY"
    // Return a Date object if it is a valid date
    if (/^[A-Za-z]{3} \d{2}, \d{4}$/.test(stringValue)) {
        const parsed = moment(stringValue, "MMM DD, YYYY", true).set("hour", 0);
        if (parsed.isValid()) return parsed.toDate();
    } else if (/^[A-Za-z]{3} \d{4}$/.test(stringValue)) {
        const parsed = moment(stringValue, "MMM YYYY", true).set("hour", 0);
        if (parsed.isValid()) return parsed.toDate();
    } else if (/^Q[1-4] \d{4}$/.test(stringValue)) {
        // Parse quarter
        const [, q, year] = stringValue.match(/^Q([1-4]) (\d{4})$/)!;
        // moment expects 0-based quarter: Q1 = 0, Q2 = 1...
        const parsed = moment()
            .year(Number(year))
            .quarter(Number(q))
            .startOf("quarter")
            .set("hour", 0);

        if (parsed.isValid()) return parsed.toDate();
    }

    return stringValue;
};

/**
 * Chart x value object where v is the unique id and f is the formatted label
 */
export type XValueType = {
    /**
     * The unique identifier of the x value.
     */
    v: string | Date;
    /**
     * The formatted label for the x value
     */
    f: string | Date;
};

/** The rendered identities emitted by the chart's selection callback. */
export type SignalChartPointIdentity = {
    x: XValueType;
    seriesIdentifier: string;
    seriesLabel: string;
};

export const getSignalChartPointKey = (point: SignalChartPointIdentity): string =>
    JSON.stringify([point.x.v, point.seriesIdentifier, point.seriesLabel]);

// Column Header type in Google Charts format
type ColumnHeader =
    | {
          id: string;
          label: string;
          type: "number";
      }
    | {
          type: "string";
          role: "tooltip";
          p: { html: true };
      };

// Header Row type for Google Charts format
type HeaderRow = ["x", ...ColumnHeader[]];

const isMissingGroupedSeriesName = (value: unknown) =>
    value == null ||
    (typeof value === "string" && value.trim().toLowerCase() === "null");

// Used for signals
// Converts the data retrieved from the Signals Chart Endpoint to
// a format that Google Charts can understand
export function signalDataToGoogleFormat(
    args: IGoogleFormatOptions,
): GoogleFormat {
    // A series identifier is a unique identifier for a series
    // A series is a grouping of data points that share a common identifier
    const seriesIdentifiersSet = new Set<string>();
    const identifierToSeriesNameMapping = new Map<string, string>();
    const identifierToLegendNameMapping = new Map<string, string>();

    // Map of available x values for the chart, v is unique id value, f is formatted label
    const xValuesMap = new Map<string, { v: string; f: string }>();

    // NOTE!
    // Maps x values, to DataPoints (DataPoints are Maps of series identifiers to y values)
    const dataPoints = new Map<string, DataPoint>();
    // The original row for each point, so the tooltip can read it
    const pointData = new Map<string, Map<string, SignalChartDatum>>();

    for (const datum of args.data) {
        // Add the x value to the set of x values
        const thisXValue = args.swapGroupingAndX
            ? datum.grouping ?? ""
            : datum.x;
        const thisXId = args.swapGroupingAndX
            ? datum.grouping_identifier ?? thisXValue
            : datum.x_identifier ?? thisXValue;

        // This ensures we have unique xValues based on their id
        // before, this was handled by the xValuesSet which ensured x labels were unique
        xValuesMap.set(thisXId, { f: thisXValue, v: thisXId });

        if (
            args.filterMissingGroupedSeries &&
            !args.swapGroupingAndX &&
            isMissingGroupedSeriesName(datum.grouping)
        ) {
            continue;
        }

        // Grouped non-temporal bar charts can intentionally flip x/grouping orientation for better readability.
        const seriesName = `${
            args.swapGroupingAndX ? datum.x ?? "" : datum.grouping ?? ""
        }`;
        const seriesIdentifierValue = args.swapGroupingAndX
            ? datum.x_identifier ?? datum.x
            : datum.grouping_identifier;
        const legendSeriesNameFromMap =
            !args.swapGroupingAndX && datum.grouping_identifier
                ? args.groupingIdentifierToLegendName?.get(
                      `${datum.grouping_identifier}`,
                  )
                : undefined;
        const legendSeriesName = legendSeriesNameFromMap ?? seriesName;
        // Determine the identifier for the series
        let thisSeriesIdentifier = "";
        if (args.useLabelsAsGroupingIds) {
            // we dont want to use the groupingId here because this chart has different groupingIds but the same label (e.g module results count tagIds)
            // drilldown is handled via rawDataResponse instead of onclick, so we want to keep groupingIds distinct on the backend
            thisSeriesIdentifier = seriesName;
        } else {
            // We have to use a combination of grouping_identifier and grouping because
            // we need to ensure uniqueness
            // (e.g. Two of the same grouping identifiers could be coming in because we are comparing to the previous period)
            thisSeriesIdentifier = `${seriesIdentifierValue ?? ""}=:=${
                seriesName
            }`;
        }
        seriesIdentifiersSet.add(thisSeriesIdentifier);

        // Map the identifier to a user readable series name (the grouping)
        identifierToSeriesNameMapping.set(
            thisSeriesIdentifier,
            seriesName,
        );
        identifierToLegendNameMapping.set(
            thisSeriesIdentifier,
            legendSeriesName,
        );

        // Get the data points available for that x value
        // And then set the data point's y value for this series identifier
        const thisDataPoint =
            dataPoints.get(thisXId) ?? new Map<string, number>();
        thisDataPoint.set(thisSeriesIdentifier, datum.y);
        dataPoints.set(thisXId, thisDataPoint);

        const thisPointData =
            pointData.get(thisXId) ?? new Map<string, SignalChartDatum>();
        thisPointData.set(thisSeriesIdentifier, datum);
        pointData.set(thisXId, thisPointData);
    }

    // We need to add null values to the result array so that they can be interpolated by the chart
    const seriesIdentifiers = Array.from(seriesIdentifiersSet);

    // We have scenarioes where we want to sort the series identifiers, based on the series names
    // First check if Eddy Not Identified is present
    // Then we sort the ids after getting the series names from the mapping
    if (
        Array.from(identifierToSeriesNameMapping.values()).includes(
            "Eddy Not Identified",
        )
    ) {
        seriesIdentifiers.sort((idA, idB) => {
            const nameA = identifierToSeriesNameMapping.get(idA) ?? "";
            const nameB = identifierToSeriesNameMapping.get(idB) ?? "";
            return nameA.localeCompare(nameB);
        });
    }

    // If comparing previous period, we need to sort the grouping ids based on the series names
    // Check if we are comparing previous period
    // Then sort by the date using the series names from the mapping
    if (args.isComparePrev) {
        seriesIdentifiers.sort((idA, idB) => {
            const nameA = identifierToSeriesNameMapping.get(idA) ?? "";
            const nameB = identifierToSeriesNameMapping.get(idB) ?? "";

            const dateA = moment(nameA.split("-")[0]);
            const dateB = moment(nameB.split("-")[0]);

            return dateA > dateB ? 1 : -1;
        });
    }

    const columnHeaders: ColumnHeader[] = [];

    for (const identifier of seriesIdentifiers) {
        // For each series identifier, we need to add a column header
        // The id of the column header is the series identifier which is found by splitting the identifier
        // on the "=:=" delimiter (actual identifier of the series is before the delimiter)
        columnHeaders.push({
            label:
                identifierToLegendNameMapping.get(identifier) ??
                identifierToSeriesNameMapping.get(identifier) ??
                identifier,
            id: identifier.split("=:=")[0],
            type: "number",
        });
        if (args.tooltipBuilder)
            columnHeaders.push({
                type: "string",
                role: "tooltip",
                p: { html: true },
            });
    }

    // Construct the final result array in the Google Charts format
    const headerRow: HeaderRow = ["x", ...columnHeaders];
    const result: (string | number | null | XValueType | any)[][] = []; // may want to clean up these types, `| any` makes the other types irrelevant

    const xValues = [...xValuesMap.values()];

    for (const xValue of xValues) {
        // result rows look like [XValue, S1Value, S2Value, S3Value]

        const resultRow: (string | number | null | XValueType | any)[] = [
            // may want to clean up these types, `| any` makes the other types irrelevant
            args.preformattedXValues
                ? { f: xValue.f, v: xValue.v }
                : { f: parseXValue(xValue.f), v: parseXValue(xValue.v) },
        ];

        for (const identifier of seriesIdentifiers) {
            const yValue = dataPoints.get(xValue.v)?.get(identifier) ?? null;
            resultRow.push(yValue);
            if (args.tooltipBuilder) {
                // google charts only likes html strings. We can still allow the user
                // to return jsx elements/nodes but use ReactDOMServer to turn it into
                // static html

                let tooltip = args.tooltipBuilder(
                    xValue.f,
                    yValue,
                    identifierToSeriesNameMapping.get(identifier) ?? identifier,
                    pointData.get(xValue.v)?.get(identifier),
                    {
                        x: resultRow[0],
                        seriesIdentifier: identifier.split("=:=")[0],
                        seriesLabel:
                            identifierToLegendNameMapping.get(identifier) ??
                            identifierToSeriesNameMapping.get(identifier) ??
                            identifier,
                    },
                );
                if (typeof tooltip !== "string")
                    tooltip = ReactDOMServer.renderToStaticMarkup(tooltip);
                resultRow.push(tooltip);
            }
        }
        result.push(resultRow);
    }

    // If the chart is temporal, we need to sort the final results array based
    // on the temporal grain and created moment values
    if (args.isTemporal) {
        result.sort((a, b) => {
            const momentA =
                args.temporalGrain === "Quarterly"
                    ? moment(a[0].f, "[Q]Q YYYY")
                    : args.temporalGrain === "Monthly"
                    ? moment(a[0].f, "MMM YYYY")
                    : moment(a[0].f);
            const momentB =
                args.temporalGrain === "Quarterly"
                    ? moment(b[0].f, "[Q]Q YYYY")
                    : args.temporalGrain === "Monthly"
                    ? moment(b[0].f, "MMM YYYY")
                    : moment(b[0].f);

            // Don't sort if the moment values are invalid
            if (!momentA.isValid() || !momentB.isValid()) return 0;

            return momentA.diff(momentB);
        });
    }

    result.unshift(headerRow);

    return {
        data: result,
        seriesNames: new Set(identifierToSeriesNameMapping.values()),
    };
}

// ---------------------------------------------------------------------------
// TreeMap data shaping
//
// Google TreeMap requires a 4-column data table: [id, parent, size, color].
// Google also requires exactly one root row whose `parent` is null. Because
// each fetch only returns one zoom level, the chart never sees a real root —
// the conversion prepends a synthetic root whose id matches whatever the
// AcxTreeMap primitive expects to re-root against (typically the deepest
// zoomPath entry's identifier, or a fixed sentinel at level 0).
// ---------------------------------------------------------------------------

export const TREE_MAP_SYNTHETIC_ROOT_ID = "__root__";

/**
 * Header used in every TreeMap data table. The first row of `data` shaped by
 * `signalDataToTreeMapFormat` is this header.
 */
export const TREE_MAP_HEADER: readonly [string, string, string, string] = [
    "Cell",
    "Parent",
    "Size",
    "Color",
];

// A cell in column 0 may be either a plain id string or Google's
// `{ v, f }` formatted-value object. The `{ v, f }` form lets us show the
// human-readable label (`x`) while keeping a different machine identifier
// (`x_identifier`, e.g. a theme GUID) for the click-through round trip.
export type TreeMapCellRef = string | { v: string; f: string };

export type TreeMapDataRow =
    | [TreeMapCellRef, string | null, number, number]
    | [string, string, string, string]; // header

// Minimum cell size as a fraction of the largest cell's size. Boosts tiny-
// volume cells so they're always large enough to render their label and be
// reasonably clickable, without distorting the ordering — the largest cell
// stays largest, and anything already above the floor is unchanged. The
// displayed percent in each cell still reads from the row's true `y_percent`,
// so the label remains accurate even when the area has been floored.
const TREE_MAP_MIN_SIZE_RATIO = 0.05;

/**
 * Shapes one level of `SignalReportDatum[]` into Google TreeMap's expected
 * 4-column table. When the row has a separate `x_identifier`, the cell uses
 * Google's `{ v: x_identifier, f: x }` form — the underlying value is the
 * identifier (round-trips on click), the formatted value is the human label
 * (rendered on the cell + tooltips). `syntheticRootId` is the parent for
 * every cell, the size column is `max(y, maxY * minRatio)` (so every cell
 * gets enough area to render its label), and `y_percent` is the color column.
 */
export function signalDataToTreeMapFormat(
    rows: SignalReportDatum[],
    syntheticRootId: string = TREE_MAP_SYNTHETIC_ROOT_ID,
): TreeMapDataRow[] {
    const maxY = rows.reduce((max, r) => {
        const y = typeof r.y === "number" && Number.isFinite(r.y) ? r.y : 0;
        return y > max ? y : max;
    }, 0);
    const minSize = maxY * TREE_MAP_MIN_SIZE_RATIO;

    return [
        [...TREE_MAP_HEADER] as TreeMapDataRow,
        [syntheticRootId, null, 0, 0] as TreeMapDataRow,
        ...rows.map<TreeMapDataRow>((r) => {
            const rawY =
                typeof r.y === "number" && Number.isFinite(r.y) ? r.y : 0;
            return [
                r.x_identifier && r.x_identifier !== r.x
                    ? { v: r.x_identifier, f: r.x }
                    : r.x_identifier ?? r.x,
                syntheticRootId,
                Math.max(rawY, minSize),
                r.y_percent ?? 0,
            ];
        }),
    ];
}
